import { create } from 'zustand';

import { AuthRepository } from '../../api/repository/auth';
import { TeacherRepository } from '../../api/repository/teacher';
import { clearQltCredentials, sessionHooks, setQltCredentials } from '../../api/session';
import { normalizeError } from '../../api/core/errors';
import { readJson, removeKey, writeJson } from '../../api/storage';
import { setBoardOwner } from '../board/owner';
import type { QltSignInResult, QltUser } from '../../api/types';
import { userErrorMessage } from '../userError';
import { SIGN_OUT_MESSAGES, rejectedSessionReason } from './signOutReason';
import type { SignOutReason } from './signOutReason';

const STORAGE_KEY = 'izqlt.qlt.auth';

// 'qr': approved from the teacher app. No board session renews: a 401 signs out.
export type QltSessionSource = 'password' | 'qr';

interface PersistedAuth {
  jwt: string;
  user: QltUser | null;
  policies: string[];
  teacherId: string | number | null;
  // absent on sessions saved before QR login existed, which were all password sessions
  source?: QltSessionSource;
  // the QLT teacher record's name; absent on sessions saved before it was kept (validateSession fills it)
  teacherName?: string | null;
}

export interface QltAuthState {
  jwt: string | null;
  user: QltUser | null;
  policies: string[];
  teacherId: string | number | null;
  /** shown instead of the login identifier, which students must not read off the board */
  teacherName: string | null;
  isTeacher: boolean;
  source: QltSessionSource | null;
  loading: boolean;
  hydrating: boolean;
  error: string | null;
  signIn: (identifier: string, password: string) => Promise<boolean>;
  /** Makes a login response the current session: credentials, teacher lookup, persistence. */
  adoptSession: (res: QltSignInResult, source: QltSessionSource) => Promise<boolean>;
  /** With a reason, the login screen shows why the server ended the session. */
  signOut: (reason?: SignOutReason) => void;
  /** Signs out with the reason a server 401/403 on this session implies. */
  signOutRejected: () => void;
  hydrate: () => void;
  validateSession: () => Promise<boolean>;
}

function persist(state: PersistedAuth | null): void {
  if (!state) {
    removeKey(STORAGE_KEY);
    return;
  }
  writeJson(STORAGE_KEY, state);
}

function persistCurrent(state: QltAuthState): void {
  const jwt = state.jwt;
  if (!jwt) return;
  persist({ jwt, user: state.user, policies: state.policies, teacherId: state.teacherId, teacherName: state.teacherName, source: state.source || 'password' });
}

// Both sign-in paths end here on failure: nothing of a half-made session may survive.
function failSignIn(error: unknown): false {
  clearQltCredentials();
  persist(null);
  useQltAuthStore.setState({ loading: false, error: userErrorMessage(error) });
  return false;
}

export const useQltAuthStore = create<QltAuthState>((set, get) => ({
  jwt: null,
  user: null,
  policies: [],
  teacherId: null,
  teacherName: null,
  isTeacher: false,
  source: null,
  loading: false,
  hydrating: false,
  error: null,

  async signIn(identifier: string, password: string) {
    set({ loading: true, error: null });
    let res: QltSignInResult;
    try {
      res = await AuthRepository.signIn(identifier, password);
    } catch (error) {
      return failSignIn(error);
    }
    return get().adoptSession(res, 'password');
  },

  async adoptSession(res: QltSignInResult, source: QltSessionSource) {
    set({ loading: true, error: null });
    try {
      const policies = res.policies || [];
      const isTeacher = Boolean(res.user && res.user.role && res.user.role.type === 'teacher');
      // The client reads the token from the session bridge, so push it before anything else.
      setQltCredentials(res.jwt, res.user ? res.user.id : null);
      const teachers = isTeacher
        ? await TeacherRepository.fetch({ user: res.user.id, _limit: 1 })
        : [];
      const teacher = teachers[0];
      const teacherId = teacher ? teacher.id || teacher._id || null : null;
      const teacherName = teacher && teacher.name ? teacher.name : null;
      persist({ jwt: res.jwt, user: res.user || null, policies, teacherId, teacherName, source });
      setBoardOwner(res.user ? res.user.id : null);
      set({
        jwt: res.jwt,
        user: res.user || null,
        policies,
        teacherId,
        teacherName,
        isTeacher,
        source,
        loading: false,
        error: null,
      });
      return true;
    } catch (error) {
      return failSignIn(error);
    }
  },

  // Keeps the board owner and its data: a phone logout mid-lesson must not lose the teacher's work.
  signOut(reason?: SignOutReason) {
    clearQltCredentials();
    persist(null);
    set({ jwt: null, user: null, policies: [], teacherId: null, teacherName: null, isTeacher: false, source: null, error: reason ? SIGN_OUT_MESSAGES[reason] : null, loading: false, hydrating: false });
    sessionHooks.onQltSignedOut();
  },

  signOutRejected() {
    const jwt = get().jwt;
    if (!jwt) return;
    get().signOut(rejectedSessionReason(jwt, Date.now()));
  },

  // Call once at boot, before any request goes out.
  hydrate() {
    if (get().jwt) return;
    const saved = readJson<PersistedAuth>(STORAGE_KEY);
    if (!saved || !saved.jwt) return;
    const source: QltSessionSource = saved.source === 'qr' ? 'qr' : 'password';
    setQltCredentials(saved.jwt, saved.user ? saved.user.id : null);
    setBoardOwner(saved.user ? saved.user.id : null);
    set({
      jwt: saved.jwt,
      user: saved.user || null,
      policies: saved.policies || [],
      teacherId: saved.teacherId || null,
      teacherName: saved.teacherName || null,
      isTeacher: Boolean(saved.user && saved.user.role && saved.user.role.type === 'teacher'),
      source,
      hydrating: true,
    });
  },

  async validateSession() {
    if (!get().jwt) {
      set({ hydrating: false });
      return false;
    }
    try {
      const user = await AuthRepository.me();
      const isTeacher = Boolean(user.role && user.role.type === 'teacher');
      set({ user, isTeacher, hydrating: false, error: null });
      if (isTeacher && !get().teacherName) void backfillTeacherName(user.id);
      return true;
    } catch (error) {
      const normalized = normalizeError(error);
      if (normalized.kind === 'unauthorized' || normalized.kind === 'forbidden') {
        get().signOutRejected();
      } else {
        set({ hydrating: false, error: 'Không thể xác minh phiên đăng nhập. Vui lòng kiểm tra kết nối.' });
      }
      return false;
    }
  },
}));

// Wire the api layer back into this store. Done once at module load - no interceptor churn.
sessionHooks.onQltAuthFailure = () => {
  useQltAuthStore.getState().signOutRejected();
};

// Sessions saved before the name was kept show the masked identifier until this lands; a failure keeps it that way.
async function backfillTeacherName(userId: string | number): Promise<void> {
  try {
    const teachers = await TeacherRepository.fetch({ user: userId, _limit: 1 });
    const name = teachers[0] && teachers[0].name ? teachers[0].name : null;
    const state = useQltAuthStore.getState();
    if (!name || !state.user || state.user.id !== userId) return;
    useQltAuthStore.setState({ teacherName: name });
    persistCurrent(useQltAuthStore.getState());
  } catch (error) {
    // the masked identifier stays
  }
}
